/**
 * Analytics › Live (brands that do live): each live event of the range with its peak at once,
 * who watched it live and who watched the replay (live + replay is the whole attendance), and the
 * range's totals against the previous period.
 */

import { SkeletonCard } from "@web-ui/components/Skeleton";
import { TileGrid } from "../components/tile-grid";
import { href } from "@/lib/route";
import { analytics, type LiveEventRow } from "../api";
import { BarList } from "../charts/bar-list";
import type { Table } from "../csv";
import { definition, type MetricId } from "../definitions";
import { liveFigure } from "../figures";
import { change, count, duration, momentLabel } from "../format";
import { MetricTile } from "../components/metric-tile";
import { NoData, ReportFailed } from "../components/no-data";
import { ReportCard } from "../components/report-card";
import { SampleNotice } from "../components/sample-notice";
import type { ReportScope } from "../scope";
import { useReport } from "../use-report";

const sum = (events: readonly LiveEventRow[], key: "replayViewers") => events.reduce((n, e) => n + e[key], 0);

export function Live({ scope }: { scope: ReportScope }) {
  const now = useReport(() => analytics.live(scope.client, scope.range), [scope.client, scope.range]);
  const before = useReport(() => analytics.live(scope.client, scope.previous), [scope.client, scope.previous]);
  const r = now.data;
  const d = (id: MetricId) => definition(id, scope.pack);
  const events = scope.pack.nouns.liveEvent.many.toLowerCase();

  if (now.failed && !r) return <section className="card"><ReportFailed /></section>;
  if (!r) return <SkeletonCard label={`Loading your ${events}`} rows={6} />;
  const b = before.data?.events ?? null;
  const rows = [...r.events].sort((x, y) => y.peak - x.peak);

  const table: Table<LiveEventRow> = {
    columns: [
      { label: "Name", value: (e) => scope.nameOf(e.itemId).label, href: (e) => scope.nameOf(e.itemId).href },
      { label: "Item id", value: (e) => e.itemId, csvOnly: true },
      { label: d("peak").label, tooltip: d("peak").tooltip, value: (e) => e.peak, format: (v) => count(Number(v)), numeric: true },
      { label: "Peak at", csvLabel: "Peak at (UTC)", value: (e) => (e.peakAt === null ? null : new Date(e.peakAt).toISOString()), format: (v) => (v === null ? "—" : momentLabel(String(v))) },
      { label: d("liveViewers").label, tooltip: d("liveViewers").tooltip, value: (e) => e.liveViewers, format: (v) => count(Number(v)), numeric: true },
      { label: d("replayViewers").label, tooltip: d("replayViewers").tooltip, value: (e) => e.replayViewers, format: (v) => count(Number(v)), numeric: true },
      { label: d("liveWatch").label, tooltip: d("liveWatch").tooltip, csvLabel: "Live watch time (seconds)", value: (e) => e.liveWatchSeconds, format: (v) => duration(Number(v)), numeric: true },
      { label: d("replayWatch").label, tooltip: d("replayWatch").tooltip, csvLabel: "Replay watch time (seconds)", value: (e) => e.replayWatchSeconds, format: (v) => duration(Number(v)), numeric: true },
    ],
    rows,
  };

  if (!rows.length) {
    return (
      <div className="flex flex-col gap-3">
        <SampleNotice shown={r.sample} />
        <section className="card"><NoData title={`No ${events} in this period`} why={`Numbers appear here once viewers watch your ${events} live or as a replay.`} action={{ label: `See your ${events}`, href: href({ brand: scope.brand, section: "live" }) }} /></section>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-3 transition-opacity ${now.stale ? "opacity-60" : ""}`} aria-busy={now.stale || undefined}>
      <SampleNotice shown={r.sample || !!before.data?.sample} />
      <TileGrid columns={3}>
        <MetricTile definition={d("peak")} value={count(liveFigure("peak", r.events))} change={change(liveFigure("peak", r.events), b ? liveFigure("peak", b) : null)} />
        <MetricTile definition={d("liveViewers")} value={count(liveFigure("liveViewers", r.events))} change={change(liveFigure("liveViewers", r.events), b ? liveFigure("liveViewers", b) : null)} />
        <MetricTile definition={d("replayViewers")} value={count(sum(r.events, "replayViewers"))} change={change(sum(r.events, "replayViewers"), b ? sum(b, "replayViewers") : null)} />
      </TileGrid>
      <ReportCard title={d("peak")} description={`Each ${scope.pack.nouns.liveEvent.one.toLowerCase()}'s highest number of people watching at the same moment.`} table={table} file={{ brand: scope.brand, what: "live", range: scope.range }}>
        <BarList label={d("peak").label} bars={rows.map((e) => {
          const item = scope.nameOf(e.itemId);
          return { key: e.itemId, label: item.label, href: item.href, value: e.peak, text: count(e.peak), detail: `${e.peakAt === null ? "Nobody watched live" : `Peak at ${momentLabel(e.peakAt)}`} · ${count(e.liveViewers)} live, ${count(e.replayViewers)} replay` };
        })} />
      </ReportCard>
    </div>
  );
}
